import type { DocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { EditDynamicSelectField } from '@/components/EditDynamicSelectField';
import { EditHrEmployeeSelectField } from '@/components/EditHrEmployeeSelectField';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const CREATE_ACCOUNT = gql`
  mutation CreateAccount($values: AccountInput!) {
    createAccount(values: $values) {
      ok
      account {
        id
        name
      }
    }
  }
`;

interface CreateAccountFormData {
  description: string;
  email: string;
  name: string;
  owner: string;
  ownerId: null | string;
  phone: string;
  relationshipType: string;
  website: string;
}

const initialFormData: CreateAccountFormData = {
  description: '',
  email: '',
  name: '',
  owner: '',
  ownerId: null,
  phone: '',
  relationshipType: '',
  website: ''
};

export interface CreateAccountDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  refetchQuery?: { query: DocumentNode; variables?: Record<string, unknown> };
}

export function CreateAccountDialog({
  isOpen,
  onOpenChange,
  refetchQuery
}: CreateAccountDialogProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] =
    useState<CreateAccountFormData>(initialFormData);

  const [createAccount] = useMutation(CREATE_ACCOUNT, {
    refetchQueries: refetchQuery ? [refetchQuery] : []
  });

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Account name is required');
      return;
    }

    setIsSaving(true);

    try {
      const values: Record<string, null | string> = {
        name: formData.name.trim()
      };

      if (formData.email.trim()) values.email = formData.email.trim();
      if (formData.phone.trim()) values.mainPhone = formData.phone.trim();
      if (formData.website.trim()) values.website = formData.website.trim();
      if (formData.relationshipType)
        values.relationshipType = formData.relationshipType;
      if (formData.ownerId) values.ownerId = formData.ownerId;
      if (formData.description.trim())
        values.description = formData.description.trim();

      const response = await createAccount({
        variables: { values }
      });

      const data = response.data as {
        createAccount?: { account?: { id: string }; ok?: boolean };
      };

      if (data.createAccount?.ok && data.createAccount.account?.id) {
        toast.success('Account created successfully');
        handleClose();
        void navigate(`/firm/crm-accounts/${data.createAccount.account.id}`);
      } else {
        toast.error('Failed to create account');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error('Failed to create account');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (
    field: keyof CreateAccountFormData,
    value: null | string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value: string): string => {
    const hasPlus = value.startsWith('+');
    const digits = value.replace(/\D/g, '');

    if (!digits) return hasPlus ? '+' : '';

    // US format: +1 (XXX) XXX-XXXX or (XXX) XXX-XXXX
    if (digits.length <= 11) {
      let formatted = '';
      let index = 0;

      // Handle country code
      if (hasPlus || digits.length === 11) {
        if (digits.length >= 1) {
          formatted = '+' + digits[0];
          index = 1;
        }
        if (digits.length > 1) {
          formatted += ' ';
        }
      }

      if (digits.length > index) {
        formatted += '(';
        formatted += digits.slice(index, index + 3);
        if (digits.length > index + 3) {
          formatted += ') ';
          formatted += digits.slice(index + 3, index + 6);
          if (digits.length > index + 6) {
            formatted += '-';
            formatted += digits.slice(index + 6, index + 10);
          }
        }
      }

      return formatted;
    }

    // For longer numbers, just add the plus and return digits
    return hasPlus ? '+' + digits : digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };

  return (
    <Dialog
      onOpenChange={open => {
        if (!open) handleClose();
        else onOpenChange(open);
      }}
      open={isOpen}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Create New Account</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            onSubmit={e => {
              void handleSubmit(e);
            }}
          >
            <div className="grid gap-4 py-4">
              <Field className="gap-2">
                <FieldLabel htmlFor="name">
                  Account Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="name"
                  onChange={e => {
                    updateField('name', e.target.value);
                  }}
                  placeholder="Enter account name"
                  value={formData.name}
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field className="gap-2">
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    onChange={e => {
                      updateField('email', e.target.value);
                    }}
                    placeholder="email@example.com"
                    type="email"
                    value={formData.email}
                  />
                </Field>
                <Field className="gap-2">
                  <FieldLabel htmlFor="phone">Phone</FieldLabel>
                  <Input
                    id="phone"
                    onChange={handlePhoneChange}
                    placeholder="+1 (555) 000-0000"
                    type="tel"
                    value={formData.phone}
                  />
                </Field>
              </div>

              <Field className="gap-2">
                <FieldLabel htmlFor="website">Website</FieldLabel>
                <Input
                  id="website"
                  onChange={e => {
                    updateField('website', e.target.value);
                  }}
                  placeholder="www.example.com"
                  value={formData.website}
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <EditDynamicSelectField
                  fieldName="relationship_type"
                  label="Relationship Type"
                  model="opstream.account"
                  onChange={value => {
                    updateField('relationshipType', value);
                  }}
                  value={formData.relationshipType}
                />
                <EditHrEmployeeSelectField
                  label="Owner"
                  onChange={(id, name) => {
                    setFormData(prev => ({
                      ...prev,
                      owner: name,
                      ownerId: id || null
                    }));
                  }}
                  value={
                    formData.ownerId
                      ? { id: formData.ownerId, name: formData.owner }
                      : null
                  }
                />
              </div>

              <Field className="gap-2">
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <Textarea
                  id="description"
                  onChange={e => {
                    updateField('description', e.target.value);
                  }}
                  placeholder="Enter a brief description of the account..."
                  rows={3}
                  value={formData.description}
                />
              </Field>
            </div>

            <DialogFooter>
              <Button
                disabled={isSaving}
                onClick={handleClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={isSaving || !formData.name.trim()}
                type="submit"
              >
                {isSaving ? 'Creating...' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
